import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/achievements", label: "Achievements" },
  { to: "/certifications", label: "Certifications" },
  { to: "/infrastructure", label: "Infrastructure" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "border-b border-white/10 bg-black/60 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <div className="flex h-20 items-center justify-between px-6 md:px-12 lg:px-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <motion.img
              src="/logo-mark.png"
              alt="Catalyst Petrochem logo"
              className="h-10 w-auto transition-[filter] duration-300 hover:drop-shadow-[0_0_10px_rgba(120,110,220,0.7)] sm:h-12 lg:h-14"
              initial={{ opacity: 0, x: -14, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            />
            <span className="font-display text-lg font-bold uppercase tracking-tight text-white">
              Catalyst Petrochem
            </span>
          </Link>
          <nav className="hidden items-center gap-7 xl:ml-16 xl:flex" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-brand" : "text-neutral-400 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <button
            className="text-white xl:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0A0A0A] px-8 xl:hidden"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ y: 40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-4 border-b border-white/5 py-4 font-display text-4xl font-semibold uppercase tracking-tight ${
                      isActive ? "text-brand" : "text-white"
                    }`
                  }
                >
                  <span className="font-mono text-xs text-brand">0{i + 1}</span>
                  {l.label}
                  <ArrowUpRight className="ml-auto h-6 w-6 text-neutral-700" />
                </NavLink>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
